import { keepPreviousData } from '@tanstack/react-query';
import type React from 'react';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { ExplorerTopLevelEntity, ExplorerViewFilters } from '../../../server/utils/explorer-utils.ts';
import { getExplorerFilterKeyByTabName } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { AppliedFilterList } from '../../filters/components/applied-filter-list.tsx';

type Props = {
    tabsToShow: ExplorerTopLevelEntity[];
    filters: ExplorerViewFilters;
    onFiltersChange: (filters: ExplorerViewFilters) => void;
    onRemoveAllFilters: () => void;
} & Omit<React.ComponentProps<typeof AppliedFilterList>, 'filters' | 'onRemoveAllFilters'>;

export function ExplorerViewAppliedFilters({
    tabsToShow,
    filters: queryFilters,
    onFiltersChange: setAppliedFilters,
    onRemoveAllFilters,
    ...props
}: Props) {
    const { t } = useLocale();

    const { data: filterLabels, isLoading } = api.platoUser.query.explorer.getFilterLabels.getLabels.useQuery(
        {
            filters: queryFilters,
        },
        {
            placeholderData: keepPreviousData,
        },
    );

    const appliedFilters = tabsToShow.flatMap((tab: ExplorerTopLevelEntity) => {
        const filterKey = getExplorerFilterKeyByTabName(tab);
        const appliedValues = queryFilters[filterKey];
        const options =
            filterLabels?.[filterKey].map((option) => ({
                label: option.name ?? '',
                value: option.id ?? '',
            })) ?? [];

        return appliedValues.map((value) => ({
            key: `${filterKey}-${value}`,
            category: t(`header_tab__${tab}`, {
                ns: 'explorer',
            }),
            filter: options.find((option) => option.value === value)?.label || '',
            onRemove: () => {
                setAppliedFilters({
                    ...queryFilters,
                    [filterKey]: appliedValues.filter((v) => v !== value),
                });
            },
        }));
    });

    if (isLoading) {
        return null;
    }

    return <AppliedFilterList filters={appliedFilters} onRemoveAllFilters={onRemoveAllFilters} {...props} />;
}
